import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AddFragranceModal from '../components/AddFragranceModal';
import FragranceModal from '../components/FragranceModal';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

const Library = ({ token }) => {
  const isMobile = useIsMobile();
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [search, setSearch] = useState('');
  const [filterAccord, setFilterAccord] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterBottle, setFilterBottle] = useState('');
  const [sortBy, setSortBy] = useState('brand');

  useEffect(() => { fetchLibrary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLibrary = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/library`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLibrary(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/library/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLibrary();
    } catch (err) { console.error(err); }
  };

  const allAccords = [...new Set(library.map(i => i.main_accord).filter(Boolean))].sort();
  const allTypes = [...new Set(library.map(i => i.type).filter(Boolean))].sort();

  const filtered = library
    .filter(item => {
      const matchSearch = !search ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.brand.toLowerCase().includes(search.toLowerCase());
      const matchAccord = !filterAccord || item.main_accord === filterAccord;
      const matchType = !filterType || item.type === filterType;
      const matchBottle =
        !filterBottle ||
        (filterBottle === 'low' && item.bottle_percentage <= 25) ||
        (filterBottle === 'medium' && item.bottle_percentage > 25 && item.bottle_percentage <= 75) ||
        (filterBottle === 'high' && item.bottle_percentage > 75);
      return matchSearch && matchAccord && matchType && matchBottle;
    })
    .sort((a, b) => {
      if (sortBy === 'brand') return a.brand.localeCompare(b.brand);
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'bottle') return a.bottle_percentage - b.bottle_percentage;
      return 0;
    });

  const hasFilters = search || filterAccord || filterType || filterBottle;

  const clearFilters = () => {
    setSearch('');
    setFilterAccord('');
    setFilterType('');
    setFilterBottle('');
  };

  const styles = {
    container: {
      minHeight: '100vh',
      padding: isMobile ? '100px 16px 48px' : '120px 48px 48px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    header: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'flex-end',
      gap: isMobile ? '20px' : '0',
      marginBottom: '32px',
      borderBottom: '1px solid #2a2a2a',
      paddingBottom: '32px',
    },
    eyebrow: {
      fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 400,
      letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '12px',
    },
    title: {
      fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2.2rem' : '3rem',
      fontWeight: 300, color: '#f5f0eb',
    },
    addButton: {
      fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 400,
      letterSpacing: '0.2em', textTransform: 'uppercase', color: '#0a0a0a',
      background: '#c9a96e', border: 'none', padding: '14px 32px', cursor: 'pointer',
      width: isMobile ? '100%' : 'auto',
    },
    empty: { textAlign: 'center', padding: '80px 0' },
    emptyTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '16px', fontStyle: 'italic' },
    emptyText: { fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: '#a09890', marginBottom: '40px' },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1px', background: '#2a2a2a', border: '1px solid #2a2a2a',
    },
    card: { background: '#111111', padding: isMobile ? '24px 20px' : '32px', cursor: 'pointer', transition: 'background 0.3s ease' },
    cardBrand: { fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', fontWeight: 400, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '8px' },
    cardName: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '16px' },
    cardType: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 300, color: '#a09890', marginBottom: '24px', letterSpacing: '0.1em' },
    bottleBar: { marginTop: 'auto' },
    bottleLabel: { display: 'flex', justifyContent: 'space-between', fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 300, color: '#a09890', marginBottom: '8px' },
    bottleTrack: { height: '2px', background: '#2a2a2a', width: '100%' },
    accord: { display: 'inline-block', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#a09890', border: '1px solid #2a2a2a', padding: '4px 10px', marginRight: '6px', marginBottom: '16px' },
  };

  const selectStyle = {
    background: '#111111',
    border: 'none',
    borderBottom: isMobile ? '1px solid #2a2a2a' : 'none',
    borderRight: isMobile ? 'none' : '1px solid #2a2a2a',
    color: '#a09890',
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.68rem',
    letterSpacing: '0.1em',
    padding: '12px 16px',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    width: isMobile ? '100%' : 'auto',
    flex: isMobile ? 'none' : 1,
  };

  if (loading) return (
    <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: '#c9a96e', fontStyle: 'italic' }}>
        Loading your collection...
      </p>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <p style={styles.eyebrow}>Your Collection</p>
          <h1 style={styles.title}>My Library</h1>
        </div>
        <button style={styles.addButton} onClick={() => setShowAddModal(true)}>Add Fragrance</button>
      </div>

      {library.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          {/* Search */}
          <input
            type="text"
            placeholder="Search by name or brand..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              background: '#111111',
              border: '1px solid #2a2a2a',
              borderBottom: 'none',
              color: '#f5f0eb',
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.9rem',
              fontWeight: 300,
              padding: '14px 24px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          {/* Filters — stack on mobile, row on desktop */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            border: '1px solid #2a2a2a',
            marginBottom: '16px',
          }}>
            <select style={selectStyle} value={filterAccord} onChange={e => setFilterAccord(e.target.value)}>
              <option value="">All Accords</option>
              {allAccords.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select style={selectStyle} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {allTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select style={selectStyle} value={filterBottle} onChange={e => setFilterBottle(e.target.value)}>
              <option value="">All Levels</option>
              <option value="low">Running Low (≤25%)</option>
              <option value="medium">Half Full (26–75%)</option>
              <option value="high">Full (76–100%)</option>
            </select>
            <select
              style={{ ...selectStyle, borderRight: 'none', borderBottom: 'none' }}
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="brand">Sort: Brand</option>
              <option value="name">Sort: Name</option>
              <option value="bottle">Sort: Bottle %</option>
            </select>
            {hasFilters && (
              <button
                onClick={clearFilters}
                style={{
                  background: 'none',
                  border: 'none',
                  borderTop: isMobile ? '1px solid #2a2a2a' : 'none',
                  borderLeft: isMobile ? 'none' : '1px solid #2a2a2a',
                  color: '#665e58',
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.6rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  padding: '12px 20px',
                  cursor: 'pointer',
                  textAlign: isMobile ? 'left' : 'center',
                }}
              >Clear Filters</button>
            )}
          </div>

          {/* Results count */}
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: hasFilters ? '#c9a96e' : '#3a3a3a' }}>
            {hasFilters ? `${filtered.length} of ${library.length} fragrances` : `${library.length} fragrance${library.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      )}

      {library.length === 0 ? (
        <div style={styles.empty}>
          <p style={styles.emptyTitle}>Your collection awaits</p>
          <p style={styles.emptyText}>Add your first fragrance to begin building your library</p>
          <button style={styles.addButton} onClick={() => setShowAddModal(true)}>Add Your First Fragrance</button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          <p style={styles.emptyTitle}>No matches found</p>
          <p style={styles.emptyText}>Try adjusting your search or filters</p>
          <button style={{ ...styles.addButton, background: 'none', border: '1px solid #2a2a2a', color: '#a09890' }} onClick={clearFilters}>Clear Filters</button>
        </div>
      ) : (
        <div style={styles.grid}>
          {filtered.map(item => (
            <div
              key={item.id}
              style={styles.card}
              onClick={() => setSelectedItem(item)}
              onMouseEnter={e => e.currentTarget.style.background = '#161616'}
              onMouseLeave={e => e.currentTarget.style.background = '#111111'}
            >
              <p style={styles.cardBrand}>{item.brand}</p>
              <p style={styles.cardName}>{item.name}</p>
              {item.type && <p style={styles.cardType}>{item.type}</p>}
              {item.main_accord && <span style={styles.accord}>{item.main_accord}</span>}
              <div style={styles.bottleBar}>
                <div style={styles.bottleLabel}>
                  <span>Bottle</span>
                  <span>{item.bottle_percentage}%</span>
                </div>
                <div style={styles.bottleTrack}>
                  <div style={{
                    height: '2px',
                    background: item.bottle_percentage > 30 ? '#c9a96e' : '#e07070',
                    width: `${item.bottle_percentage}%`,
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddFragranceModal token={token} onClose={() => setShowAddModal(false)} onSuccess={fetchLibrary} />
      )}
      {selectedItem && (
        <FragranceModal
          item={selectedItem}
          token={token}
          onClose={() => setSelectedItem(null)}
          onDelete={(id) => { handleDelete(id); setSelectedItem(null); }}
        />
      )}
    </div>
  );
};

export default Library;